import React, { useState, useEffect } from 'react';
import 'App.css';
import { Container, Row, Col, Form, Button, Table } from 'react-bootstrap';

const SERVER_URL = window.location.port === '3000'
  ? 'http://127.0.0.1:5000'
  : `${window.location.protocol}//${window.location.hostname}:${window.location.port}`;

const rankDict: { [key: string]: number } = {
  'AA': 5,
  'A': 4,
  'AB': 3,
  'B': 2,
  'C': 1
};

interface Result {
  name: string;
  shop: string;
  description: string;
  url: string;
  image_url: string;
  last_update: string;
  price: string;
  rank: string;
}

const App: React.FC = () => {
  const [keyword, setKeyword] = useState('');
  const [rankFilter, setRankFilter] = useState('等級フィルター');
  const [nameFilter, setNameFilter] = useState('');
  const [result, setResult] = useState<Result[]>([]);
  const [shopFilter, setShopFilter] = useState('店名フィルター');
  const [shopList, setShopList] = useState<string[]>(['店名フィルター']);

  const search = async () => {
    const res = await fetch(`${SERVER_URL}/api/search?keyword=${keyword}&count=1000`);
    if (res.ok) {
      setResult(await res.json());
    }
  };

  useEffect(() => {
    const rawList = result.map(r => r.shop);
    const distinctList = Array.from(new Set(rawList));
    setShopList(['店名フィルター', ...distinctList.sort()]);
  }, [result]);

  const isValid = (record: Result): boolean => {
    // 等級による判定
    if (rankFilter !== '等級フィルター' && rankDict[record.rank] < rankDict[rankFilter]) {
      return false;
    }

    // 店名による判定
    if (shopFilter !== '店名フィルター' && record.shop !== shopFilter) {
      return false;
    }

    // 商品名による判定
    if (nameFilter !== '' && !record.name.includes(nameFilter)) {
      return false;
    }
    return true;
  };

  return (
    <Container>
      <Row className="mt-5">
        <Col className="text-center">
          <h1>キタムラ検索</h1>
        </Col>
      </Row>
      <Row className="mt-3">
        <Col className="mx-auto text-center" sm={6}>
          <Form>
            <Form.Group as={Row} sm="2">
              <Col sm={10} className="mt-3">
                <Form.Control value={keyword} placeholder="検索ワードを入力"
                  onChange={(e: React.FormEvent<HTMLInputElement>) => setKeyword(e.currentTarget.value)} />
              </Col>
              <Col sm={2} className="mt-3">
                <Button className="w-100" onClick={search}>検索</Button>
              </Col>
            </Form.Group>
          </Form>
        </Col>
      </Row>
      <hr />
      <Row className="mt-3">
        <Col className="mx-auto text-center" sm={6}>
          <Form>
            <Form.Group as={Row}>
              <Col>
                <Form.Control as="select" value={rankFilter}
                  onChange={(e: React.FormEvent<HTMLInputElement>) => setRankFilter(e.currentTarget.value)}>
                  <option value="等級フィルター">等級フィルター</option>
                  <option value="AA">AA以上</option>
                  <option value="A">A以上</option>
                  <option value="AB">AB以上</option>
                  <option value="B">B以上</option>
                  <option value="C">C以上</option>
                </Form.Control>
              </Col>
              <Col>
                <Form.Control value={nameFilter} placeholder="商品名フィルター"
                  onChange={(e: React.FormEvent<HTMLInputElement>) => setNameFilter(e.currentTarget.value)} />
              </Col>
              <Col>
                <Form.Control as="select" value={shopFilter}
                  onChange={(e: React.FormEvent<HTMLInputElement>) => setShopFilter(e.currentTarget.value)}>
                  {
                    shopList.map(shop => {
                      return (<option value={shop}>{shop}</option>);
                    })
                  }
                </Form.Control>
              </Col>
            </Form.Group>
          </Form>
        </Col>
      </Row>
      <hr />
      <Row className="mt-3">
        <Col className="mx-auto text-center">
          <h3>検索結果：{result.length}件</h3>
        </Col>
      </Row>
      <Row className="my-3 d-block d-sm-none">
        <Col className="mx-auto text-center">
          <Table size="sm" bordered>
            <thead>
              <tr>
                <th className="text-nowrap">画像</th>
                <th className="text-nowrap">価格</th>
                <th className="text-nowrap">等級</th>
              </tr>
              <tr>
                <th colSpan={3} className="text-nowrap">店名</th>
              </tr>
              <tr>
                <th colSpan={3} className="text-nowrap">商品名</th>
              </tr>
              <tr>
                <th colSpan={3} className="text-nowrap">詳細</th>
              </tr>
            </thead>
            <tbody>
              {
                result.length > 0
                  ? result.filter(record => isValid(record)).sort((a, b) => parseInt(a.price, 10) - parseInt(b.price, 10)).map(record => {
                    return (<>
                      <tr key={record.url + '-0'}>
                        <td colSpan={3} style={{ height: 10, backgroundColor: 'gray' }}></td>
                      </tr>
                      <tr key={record.url + '-1'}>
                        <td><img src={record.image_url} alt={record.name} height={40} /></td>
                        <td>{record.price}</td>
                        <td>{record.rank}</td>
                      </tr>
                      <tr key={record.url + '-2'}>
                        <td colSpan={3}>{record.shop}</td>
                      </tr>
                      <tr key={record.url + '-3'}>
                        <td colSpan={3}><a href={record.url} rel="noopener noreferrer" target="_blank">{record.name}</a></td>
                      </tr>
                      <tr key={record.url + '-4'}>
                        <td colSpan={3}>{record.description}</td>
                      </tr>
                    </>);
                  })
                  : <tr>
                    <td colSpan={6}>検索にヒットしませんでした。</td>
                  </tr>
              }
            </tbody>
          </Table>
        </Col>
      </Row>
      <Row className="my-3 d-none d-sm-block">
        <Col className="mx-auto text-center">
          <Table bordered>
            <thead>
              <tr>
                <th className="text-nowrap">画像</th>
                <th className="text-nowrap">価格</th>
                <th className="text-nowrap">等級</th>
                <th className="text-nowrap">商品名</th>
                <th className="text-nowrap">詳細</th>
                <th className="text-nowrap">店名</th>
              </tr>
            </thead>
            <tbody>
              {
                result.length > 0
                  ? result.filter(record => isValid(record)).sort((a, b) => parseInt(a.price, 10) - parseInt(b.price, 10)).map(record => {
                    return (<tr key={record.url}>
                      <td><img src={record.image_url} alt={record.name} height={40} /></td>
                      <td>{record.price}</td>
                      <td>{record.rank}</td>
                      <td><a href={record.url} rel="noopener noreferrer" target="_blank">{record.name}</a></td>
                      <td>{record.description}</td>
                      <td>{record.shop}</td>
                    </tr>);
                  })
                  : <tr>
                    <td colSpan={6}>検索にヒットしませんでした。</td>
                  </tr>
              }
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
}

export default App;
